// @flow
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { colorContentDim } from '@duxton-web/design-tokens/themes/light';
import {
  Alert,
  Checkbox,
  Form,
  Modal,
  Select,
  Typography,
} from '@duxton-web/ui';

import supportedLanguages from 'omega-rtc/data/languages.json';

const primaryLanguageSelectorKey = 'primaryLanguage';

type LanguageSelectorModalProps = {
  isOpen: boolean,
  existingPrimaryLanguage: string,
  defaultSelectedLanguages: string[],
  isPrimaryLanguageSelectorDisabled: boolean,
  isManualMode: boolean,
  onCancel: () => void,
  onSave: (languages: string[]) => void,
};

const LanguageSelectorModal = ({
  isOpen,
  isPrimaryLanguageSelectorDisabled,
  existingPrimaryLanguage,
  defaultSelectedLanguages,
  onCancel,
  onSave,
  isManualMode = false,
}: LanguageSelectorModalProps) => {
  const form = Form.useFormInstance();

  const [selectedLanguages, setSelectedLanguages] = useState(
    defaultSelectedLanguages,
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelectedLanguages(defaultSelectedLanguages);
    form.setFieldValue(primaryLanguageSelectorKey, existingPrimaryLanguage);
  }, [isOpen]);

  const primaryLanguage = Form.useWatch(primaryLanguageSelectorKey);

  const languageOptionsForCheckbox = useMemo(() => {
    const languageCodes = _.union(
      [primaryLanguage || existingPrimaryLanguage],
      _.keys(supportedLanguages),
    ); // force primaryLanguage to be the first.

    return languageCodes.map((languageCode) => ({
      label: supportedLanguages[languageCode].label,
      value: supportedLanguages[languageCode].code,
      disabled:
        supportedLanguages[languageCode].code ===
        (primaryLanguage || existingPrimaryLanguage), // To prevent primaryLanguage from unselected.
    }));
  }, [primaryLanguage]);

  const primaryLanguageSelectorOptions = useMemo(() => {
    return selectedLanguages.map((selectedLanguageCode) => ({
      label: supportedLanguages[selectedLanguageCode].label,
      value: supportedLanguages[selectedLanguageCode].code,
    }));
  }, [selectedLanguages]);

  const message = isManualMode
    ? 'You will lose the existing content variants after you save the changes'
    : `You will lose the ${supportedLanguages[existingPrimaryLanguage].label} generated content variants after you save the changes`;

  return (
    <Modal
      open={isOpen}
      centered
      width={'auto'}
      style={{ padding: 24, minWidth: 600 }}
      title="Select Languages"
      okText="Save"
      cancelButtonProps={{ ghost: true }}
      onCancel={onCancel}
      onOk={() => {
        const changedPrimaryLanguage =
          primaryLanguage || form.getFieldValue(primaryLanguageSelectorKey);
        const languages = _.union([changedPrimaryLanguage], selectedLanguages); // force primaryLanguage to be the first.
        onSave(languages);
      }}
    >
      <Checkbox.Group
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          rowGap: 20,
          columnGap: 32,
          marginTop: 24,
        }}
        options={languageOptionsForCheckbox}
        value={selectedLanguages}
        onChange={(values) => {
          setSelectedLanguages(values);
        }}
      />

      {/* Primary language selector */}
      {selectedLanguages.length > 1 && (
        <React.Fragment>
          <div style={{ marginTop: 24, marginBottom: 24 }}>
            <Form.Item
              style={{ marginBottom: 8 }}
              labelCol={{ span: 24 }}
              label="Primary language"
              name={primaryLanguageSelectorKey}
              rules={[{ required: true }]}
              required
            >
              <Select
                style={{ width: '100%' }}
                disabled={isPrimaryLanguageSelectorDisabled}
                options={primaryLanguageSelectorOptions}
              />
            </Form.Item>
            <Typography.Metadata level={1} style={{ color: colorContentDim }}>
              Choose the language you want to generate content for
            </Typography.Metadata>
          </div>

          {/* TODO: add check whether any content is already generated. */}
          {primaryLanguage && existingPrimaryLanguage !== primaryLanguage && (
            <Alert
              style={{ width: 552, padding: 16, borderRadius: 16 }}
              message={message}
              description='Please click "Cancel" and save a backup in your notebook if you want to keep the content.'
              type="warning"
              banner
              showIcon
            />
          )}
        </React.Fragment>
      )}
    </Modal>
  );
};

export default LanguageSelectorModal;
